const isMobileDevice = () => {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia === 'function') {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(hover: none)').matches
    )
  }
  return 'ontouchstart' in window
}

export const initMaps = async () => {
  const geometryBounds = (geometry) => {
    if (!geometry || !geometry.type) return null

    const bounds = {
      minLng: Infinity,
      minLat: Infinity,
      maxLng: -Infinity,
      maxLat: -Infinity,
      hasPoint: false,
    }

    const addPoint = (coord) => {
      if (!Array.isArray(coord) || coord.length < 2) return
      const lng = Number(coord[0])
      const lat = Number(coord[1])
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
      bounds.minLng = Math.min(bounds.minLng, lng)
      bounds.minLat = Math.min(bounds.minLat, lat)
      bounds.maxLng = Math.max(bounds.maxLng, lng)
      bounds.maxLat = Math.max(bounds.maxLat, lat)
      bounds.hasPoint = true
    }

    const walkCoords = (coords) => {
      if (!Array.isArray(coords) || !coords.length) return
      if (typeof coords[0] === 'number') {
        addPoint(coords)
        return
      }
      coords.forEach(walkCoords)
    }

    if (geometry.type === 'GeometryCollection') {
      ;(geometry.geometries || []).forEach((g) => {
        const child = geometryBounds(g)
        if (!child) return
        bounds.minLng = Math.min(bounds.minLng, child.minLng)
        bounds.minLat = Math.min(bounds.minLat, child.minLat)
        bounds.maxLng = Math.max(bounds.maxLng, child.maxLng)
        bounds.maxLat = Math.max(bounds.maxLat, child.maxLat)
        bounds.hasPoint = true
      })
    } else {
      walkCoords(geometry.coordinates)
    }

    if (!bounds.hasPoint) return null
    return bounds
  }

  const pointInRect = (lng, lat, rect) => {
    return (
      lng >= rect.west &&
      lng <= rect.east &&
      lat >= rect.south &&
      lat <= rect.north
    )
  }

  const segmentIntersectsSegment = (a, b, c, d) => {
    const orient = (p, q, r) => {
      const val =
        (q.lat - p.lat) * (r.lng - q.lng) - (q.lng - p.lng) * (r.lat - q.lat)
      if (val === 0) return 0
      return val > 0 ? 1 : 2
    }

    const onSegment = (p, q, r) => {
      return (
        q.lng <= Math.max(p.lng, r.lng) &&
        q.lng >= Math.min(p.lng, r.lng) &&
        q.lat <= Math.max(p.lat, r.lat) &&
        q.lat >= Math.min(p.lat, r.lat)
      )
    }

    const o1 = orient(a, b, c)
    const o2 = orient(a, b, d)
    const o3 = orient(c, d, a)
    const o4 = orient(c, d, b)

    if (o1 !== o2 && o3 !== o4) return true

    if (o1 === 0 && onSegment(a, c, b)) return true
    if (o2 === 0 && onSegment(a, d, b)) return true
    if (o3 === 0 && onSegment(c, a, d)) return true
    if (o4 === 0 && onSegment(c, b, d)) return true

    return false
  }

  const lineIntersectsRect = (coords, rect) => {
    if (!Array.isArray(coords) || coords.length < 2) return false

    const rectEdges = [
      [
        { lng: rect.west, lat: rect.south },
        { lng: rect.east, lat: rect.south },
      ],
      [
        { lng: rect.east, lat: rect.south },
        { lng: rect.east, lat: rect.north },
      ],
      [
        { lng: rect.east, lat: rect.north },
        { lng: rect.west, lat: rect.north },
      ],
      [
        { lng: rect.west, lat: rect.north },
        { lng: rect.west, lat: rect.south },
      ],
    ]

    for (let i = 1; i < coords.length; i += 1) {
      const a = coords[i - 1]
      const b = coords[i]
      if (!Array.isArray(a) || !Array.isArray(b)) continue
      const aLng = Number(a[0])
      const aLat = Number(a[1])
      const bLng = Number(b[0])
      const bLat = Number(b[1])
      if (
        !Number.isFinite(aLng) ||
        !Number.isFinite(aLat) ||
        !Number.isFinite(bLng) ||
        !Number.isFinite(bLat)
      ) {
        continue
      }

      if (pointInRect(aLng, aLat, rect) || pointInRect(bLng, bLat, rect)) {
        return true
      }

      const segMinLng = Math.min(aLng, bLng)
      const segMaxLng = Math.max(aLng, bLng)
      const segMinLat = Math.min(aLat, bLat)
      const segMaxLat = Math.max(aLat, bLat)
      if (
        segMaxLng < rect.west ||
        segMinLng > rect.east ||
        segMaxLat < rect.south ||
        segMinLat > rect.north
      ) {
        continue
      }

      const p1 = { lng: aLng, lat: aLat }
      const p2 = { lng: bLng, lat: bLat }

      for (let e = 0; e < rectEdges.length; e += 1) {
        const [q1, q2] = rectEdges[e]
        if (segmentIntersectsSegment(p1, p2, q1, q2)) return true
      }
    }

    return false
  }

  const geometryIntersectsViewport = (geometry, mapBounds) => {
    if (!geometry || !geometry.type || !mapBounds) return false

    const rect = {
      west: mapBounds.getWest(),
      east: mapBounds.getEast(),
      south: mapBounds.getSouth(),
      north: mapBounds.getNorth(),
    }

    const bbox = geometryBounds(geometry)
    if (!intersectsViewport(bbox, mapBounds)) return false

    const coords = geometry.coordinates

    if (geometry.type === 'Point') {
      return (
        Array.isArray(coords) &&
        pointInRect(Number(coords[0]), Number(coords[1]), rect)
      )
    }

    if (geometry.type === 'MultiPoint') {
      return (coords || []).some(
        (pt) =>
          Array.isArray(pt) && pointInRect(Number(pt[0]), Number(pt[1]), rect),
      )
    }

    if (geometry.type === 'LineString') {
      return lineIntersectsRect(coords || [], rect)
    }

    if (geometry.type === 'MultiLineString') {
      return (coords || []).some((line) => lineIntersectsRect(line || [], rect))
    }

    if (geometry.type === 'GeometryCollection') {
      return (geometry.geometries || []).some((child) =>
        geometryIntersectsViewport(child, mapBounds),
      )
    }

    // For polygons we keep bbox semantics as a practical fallback; line
    // false-positives are the common issue and are handled above.
    return true
  }

  const intersectsViewport = (featureBounds, mapBounds) => {
    if (!featureBounds || !mapBounds) return false
    const west = mapBounds.getWest()
    const east = mapBounds.getEast()
    const south = mapBounds.getSouth()
    const north = mapBounds.getNorth()

    return !(
      featureBounds.maxLng < west ||
      featureBounds.minLng > east ||
      featureBounds.maxLat < south ||
      featureBounds.minLat > north
    )
  }

  const attachHomepageAsideInitiatives = (el, map, data) => {
    if (!el || !map || !data || !Array.isArray(data.features)) return

    const article = el.closest('.article') || document
    const mapSection = el.closest('section')
    const legendTags = Array.from(
      article.querySelectorAll('[data-home-map-category-filter]'),
    )
    const defaultPanel = article.querySelector('[data-home-aside-default]')
    const mapPanel = article.querySelector('[data-home-aside-map]')
    const backBtn = article.querySelector('[data-home-aside-back]')
    const listEl = article.querySelector('[data-home-map-initiatives]')
    if (!listEl) return
    const emptyEl = article.querySelector('[data-home-map-initiatives-empty]')

    let isMapPanelActive = false
    let hasUserInteracted = false
    let hoveredInitiativeKey = ''
    let activeCategoryColor = ''

    const initiativeKeyFrom = (initiative) => {
      if (!initiative) return ''
      return (
        initiative.url ||
        `${initiative.title || ''}:${initiative.is_residency ? '1' : '0'}`
      )
    }

    const buildFeatureMeta = (sourceData) => {
      const rows = Array.isArray(sourceData && sourceData.features)
        ? sourceData.features
        : []

      return rows.map((feature) => {
        return {
          props: (feature && feature.properties) || {},
          geometry: feature && feature.geometry,
          bounds: geometryBounds(feature && feature.geometry),
        }
      })
    }

    let features = buildFeatureMeta(data)

    const escapeHtml = (value) => {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }

    const sanitizeUrl = (value) => {
      const url = String(value || '').trim()
      if (!url) return '#'
      if (
        url.startsWith('/') ||
        url.startsWith('./') ||
        url.startsWith('../')
      ) {
        return url
      }
      if (/^https?:\/\//i.test(url)) return url
      return '#'
    }

    const collectLayersByInitiative = () => {
      const byKey = new Map()

      const indexLayer = (layer) => {
        const props = layer && layer.feature && layer.feature.properties
        const initiatives = Array.isArray(props && props.initiatives)
          ? props.initiatives
          : []
        initiatives.forEach((initiative) => {
          const key = initiativeKeyFrom(initiative)
          if (!key) return
          if (!byKey.has(key)) byKey.set(key, new Set())
          byKey.get(key).add(layer)
        })
      }

      const walkLayer = (layer) => {
        if (!layer) return
        indexLayer(layer)
        if (typeof layer.eachLayer === 'function') {
          layer.eachLayer((child) => walkLayer(child))
        }
      }

      map.eachLayer((layer) => walkLayer(layer))
      return byKey
    }

    let layersByInitiative = collectLayersByInitiative()

    const setLayerHoverState = (layer, isActive) => {
      if (!layer) return

      if (typeof layer.getElement === 'function') {
        const el = layer.getElement()
        const marker = el && el.querySelector('.tc-marker')
        if (marker) {
          if (!isActive) {
            if (
              typeof layer.isPopupOpen === 'function' &&
              layer.isPopupOpen()
            ) {
              return
            }
          }
          marker.classList.toggle('tc-marker--active', isActive)
          return
        }
      }

      if (typeof layer.fire === 'function') {
        layer.fire(isActive ? 'mouseover' : 'mouseout')
      }
    }

    const setHoveredInitiative = (key) => {
      if (hoveredInitiativeKey === key) return

      if (
        hoveredInitiativeKey &&
        layersByInitiative.has(hoveredInitiativeKey)
      ) {
        layersByInitiative.get(hoveredInitiativeKey).forEach((layer) => {
          setLayerHoverState(layer, false)
        })
      }

      hoveredInitiativeKey = key || ''

      if (
        hoveredInitiativeKey &&
        layersByInitiative.has(hoveredInitiativeKey)
      ) {
        layersByInitiative.get(hoveredInitiativeKey).forEach((layer) => {
          setLayerHoverState(layer, true)
        })
      }
    }

    const syncFilteredSources = () => {
      const sourceData = map._tcFilteredSourceData || data
      features = buildFeatureMeta(sourceData)
      layersByInitiative = collectLayersByInitiative()
    }

    const syncLegendState = () => {
      legendTags.forEach((tagEl) => {
        const tagColor = String(tagEl.dataset.homeMapCategoryFilter || '')
          .trim()
          .toLowerCase()
        const isActive = tagColor === activeCategoryColor
        tagEl.classList.toggle('is-active', isActive)
        tagEl.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      })
    }

    const renderRows = (rows) => {
      listEl.innerHTML = rows
        .map((row) => {
          const title = escapeHtml(row.title || '')
          const url = sanitizeUrl(row.url)
          const icon = row.is_residency ? 'tc:teren-comun' : 'tc:satelit'
          const key = escapeHtml(row.key || '')
          const logoUrl = sanitizeUrl(row.logoUrl)
          const dots = Array.isArray(row.colors) ? row.colors : []
          const types = Array.isArray(row.types) ? row.types : []
          const iconHtml =
            row.is_residency && logoUrl !== '#'
              ? `<img src="${logoUrl}" alt="${title} logo" />`
              : `<iconify-icon icon="${icon}"></iconify-icon>`
          const dotsHtml = dots
            .map((color) => {
              const colorClass = String(color || '')
                .trim()
                .toLowerCase()
              if (!/^[a-z0-9_-]+$/i.test(colorClass)) return ''
              return `<span class="tag is-${colorClass}"><span class="tag__dot"></span></span>`
            })
            .join('')

          const typesHtml = types.length
            ? `<p class="text-14 mt-8">${escapeHtml(types.join(', '))}</p>`
            : ''

          return `
            <a href="${url}" class="box-initiative mb-8" data-initiative-key="${key}">
              <p class="icon-text">
                <span class="text-21 fw-600">${title}</span>
                <span class="icon icon--28 ml-auto" aria-hidden="true">${iconHtml}</span>
              </p>
              ${typesHtml}
              ${dotsHtml ? `<div class="icon-text is-narrow mt-8">${dotsHtml}</div>` : ''}
            </a>`
        })
        .join('')
    }

    const showDefaultPanel = () => {
      isMapPanelActive = false
      if (defaultPanel) defaultPanel.hidden = false
      if (mapPanel) mapPanel.hidden = true
    }

    const scrollToMapSectionTop = (force = false) => {
      if (!force && !isMobileDevice()) return
      if (mapSection) {
        const prefersReducedMotion =
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        mapSection.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start',
        })
      }
    }

    const showMapPanel = () => {
      isMapPanelActive = true
      if (defaultPanel) defaultPanel.hidden = true
      if (mapPanel) mapPanel.hidden = false
    }

    const updateAside = () => {
      const viewBounds = map.getBounds()
      const unique = new Map()

      features.forEach((item) => {
        if (!geometryIntersectsViewport(item.geometry, viewBounds)) return
        const initiatives = Array.isArray(item.props.initiatives)
          ? item.props.initiatives
          : []

        initiatives.forEach((initiative) => {
          if (!initiative || !initiative.title) return
          const key =
            initiative.url ||
            `${initiative.title}:${initiative.is_residency ? '1' : '0'}`
          const colors = Array.isArray(initiative.colors)
            ? initiative.colors.filter(Boolean)
            : []
          const types = Array.isArray(initiative.types)
            ? initiative.types.filter(Boolean)
            : []

          if (unique.has(key)) {
            const existing = unique.get(key)
            existing.colors = Array.from(
              new Set([...(existing.colors || []), ...colors]),
            )
            existing.types = Array.from(
              new Set([...(existing.types || []), ...types]),
            )
            if (!existing.logoUrl && initiative.logo_url) {
              existing.logoUrl = initiative.logo_url
            }
            return
          }

          unique.set(key, {
            key,
            title: initiative.title,
            url: initiative.url || '#',
            is_residency: Boolean(initiative.is_residency),
            logoUrl: initiative.logo_url || '',
            colors,
            types,
          })
        })
      })

      const rows = Array.from(unique.values()).sort((a, b) =>
        a.title.localeCompare(b.title, 'ro-RO'),
      )
      renderRows(rows)

      if (emptyEl) {
        emptyEl.hidden = rows.length > 0
      }
    }

    const onMapChanged = () => {
      if (!hasUserInteracted) return
      if (!isMapPanelActive) showMapPanel()
      scrollToMapSectionTop()
      syncFilteredSources()
      updateAside()
    }

    map.on('dragstart zoomstart', () => {
      hasUserInteracted = true
    })

    map.on('moveend zoomend', onMapChanged)

    map.on('popupopen', () => {
      hasUserInteracted = true
      onMapChanged()
    })

    if (backBtn) {
      backBtn.addEventListener('click', showDefaultPanel)
    }

    legendTags.forEach((tagEl) => {
      tagEl.addEventListener('click', () => {
        const color = String(tagEl.dataset.homeMapCategoryFilter || '')
          .trim()
          .toLowerCase()
        const nextColor = color === activeCategoryColor ? '' : color

        activeCategoryColor = nextColor
        setHoveredInitiative('')

        if (typeof map.setCategoryFilter === 'function') {
          map.setCategoryFilter(nextColor)
        }

        hasUserInteracted = true
        syncLegendState()
        syncFilteredSources()
        scrollToMapSectionTop(true)
        onMapChanged()
      })
    })

    const canHover =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(hover: hover)').matches

    if (canHover) {
      listEl.addEventListener('mouseover', (ev) => {
        const row = ev.target.closest('[data-initiative-key]')
        if (!row || !listEl.contains(row)) return
        setHoveredInitiative(row.getAttribute('data-initiative-key') || '')
      })

      listEl.addEventListener('mouseout', (ev) => {
        const row = ev.target.closest('[data-initiative-key]')
        if (!row || !listEl.contains(row)) return
        const to = ev.relatedTarget
        if (to && row.contains(to)) return
        setHoveredInitiative('')
      })
    }

    syncLegendState()
    showDefaultPanel()
  }

  const containers = document.querySelectorAll('[data-tc-map]')
  if (!containers.length) return

  // Lazy-load teren-map (pulls in leaflet + leaflet.markercluster + their CSS).
  const [TerenMap] = await Promise.all([
    import(/* webpackChunkName: "teren-map" */ '@/cms/js/teren-map'),
  ])

  containers.forEach((el) => {
    const type = el.dataset.tcMap
    const dataId = el.dataset.tcMapData
    const dataEl = dataId ? document.getElementById(dataId) : null
    const data = dataEl
      ? JSON.parse(dataEl.textContent)
      : { type: 'FeatureCollection', features: [] }

    if (type === 'full') {
      const map = TerenMap.initFullMap({ el, data })
      if (!map) return
      attachHomepageAsideInitiatives(el, map, data)
    } else {
      TerenMap.initBlock({ el, data })
    }
  })
}
